import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Drawer, Dropdown, Form, InputNumber, List, Modal, Popconfirm, Select, Space, Tag, Tooltip, Typography, Upload, message } from 'antd';
import { saveAs } from 'file-saver';
import {
  DownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  FilePdfOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  UploadOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { useNavigate } from 'react-router-dom';
import { createExtraInspectionTask } from 'modules/inspection-tasks/services/inspection-taskService';
import {
  downloadControlTaskReport,
  deleteControlTaskBulkGroup,
  fetchControlTaskReportArchiveStatus,
  fetchControlTaskAnswers,
  fetchControlTaskBulkCapacity,
  fetchControlTaskBulkGroups,
  publishBulkControlTaskReport,
  publishControlTaskReport,
  requestControlTaskReportArchive,
  uploadSignedControlTaskReport,
  type ControlTaskBulkCapacity,
  type ControlTaskBulkGroup,
  type ControlTaskReportArchiveJob,
} from 'modules/inspectors/services/controlTaskService';
import {
  resolveBulkGroupReportCount,
} from 'modules/inspectors/utils/bulkReportLimit';

const { Text } = Typography;
const REPORT_ARCHIVE_POLL_INTERVAL_MS = 1000;
const REPORT_ARCHIVE_MAX_POLL_ATTEMPTS = 300;

interface InspectorPlanTasksCardProps {
  tasks: any[];
  archiveTasks?: any[];
  attributeNameById: Map<number, string>;
  reportFormatStatusByAttributeId?: Map<number, ReportFormatStatus>;
  proposalId?: number | null;
  className?: string;
  title?: string;
  attributes?: any[];
  tools?: any[];
  appointments?: any[];
  inspectors?: any[];
  currentAccountId?: number;
  isInspector?: boolean;
  canSelectAllPlanInspectors?: boolean;
  onExtraTaskCreated?: () => void;
}

type ControlItem = {
  key: string;
  displayOrder: number;
  orderNumber: number;
  taskId?: number;
  attributeId?: number;
  rawTask: any;
  hasSavedResponse: boolean;
  isExtra: boolean;
  isCorrection: boolean;
  correctionCaseType?: CorrectionCaseType;
  correctionCaseId?: number;
  correctionStatus?: string;
  sourceReportNumber?: string;
  reportUrl?: string | null;
  signedReportUrl?: string | null;
  reportApproved?: boolean | null;
  reportNumber?: string | null;
  hasNonconformity: boolean;
};

type ResolvedTask = {
  key: string;
  attributeName: string;
  qtyCount: number;
  statuses: string[];
  attributeId?: number;
  componentId?: number;
  componentName?: string;
  bulkReportEntry: boolean;
  controlItems: ControlItem[];
  savedResponseCount: number;
  hasSavedResponse: boolean;
  hasExtra: boolean;
  correctionCaseTypes: CorrectionCaseType[];
};

type ExtraTaskMode = 'add' | 'increase';
type CorrectionCaseType = 'CORRECTION' | 'FOLLOW_UP';
type UploadedReportOverride = {
  signedReportUrl?: string | null;
  reportApproved?: boolean | null;
  reportNumber?: string | null;
};

type BulkProgressSummary = {
  savedResponseCount: number;
  reportCount: number;
};

type ReportDownloadSummary = {
  requested: number;
  success: number;
  failed: number;
};

const resolveBulkProgressSummary = (groups: ControlTaskBulkGroup[]): BulkProgressSummary =>
  groups.reduce<BulkProgressSummary>(
    (summary, group) => ({
      savedResponseCount: summary.savedResponseCount + Math.max(Math.floor(Number(group.savedResponseCount) || 0), 0),
      reportCount: summary.reportCount + resolveBulkGroupReportCount(group),
    }),
    { savedResponseCount: 0, reportCount: 0 },
  );

type TaskComponentGroup = {
  key: string;
  componentName: string;
  tasks: ResolvedTask[];
};

const EMPTY_ITEMS: any[] = [];

export type ReportFormatStatus = {
  hasReportFormat: boolean;
  isActive: boolean;
  isTested: boolean;
};

const statusLabelMap: Record<string, string> = {
  PLANNED: 'PLANLANDI',
};

const resolveStatusLabel = (status: string): string => {
  const normalizedStatus = status.trim().toUpperCase();
  return statusLabelMap[normalizedStatus] ?? status;
};

const toPositiveInteger = (value: unknown): number | undefined => {
  if (value == null) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return Math.floor(numeric);
};

const toPositiveNumber = (value: unknown): number | undefined => {
  if (value == null) {
    return undefined;
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return undefined;
  }
  return numeric;
};

const toOptionalString = (value: unknown): string | null => {
  if (typeof value !== 'string') {
    return null;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const resolveReportUrl = (source: any): string | null =>
  toOptionalString(source?.reportUrl ?? source?.report_url ?? source?.response?.reportUrl ?? source?.controlTaskResponse?.reportUrl);

const resolveSignedReportUrl = (source: any): string | null =>
  toOptionalString(
    source?.signedReportUrl ??
      source?.signed_report_url ??
      source?.response?.signedReportUrl ??
      source?.response?.signed_report_url ??
      source?.controlTaskResponse?.signedReportUrl ??
      source?.controlTaskResponse?.signed_report_url,
  );

const resolveReportApproved = (source: any): boolean | null => {
  const value = source?.reportApproved ?? source?.report_approved ?? source?.response?.reportApproved ?? source?.controlTaskResponse?.reportApproved;
  return typeof value === 'boolean' ? value : null;
};

const resolveReportNumber = (source: any): string | null =>
  toOptionalString(source?.reportNumber ?? source?.report_number ?? source?.response?.reportNumber ?? source?.controlTaskResponse?.reportNumber);

const resolveHasNonconformity = (source: any): boolean =>
  source?.hasNonconformity === true || source?.has_nonconformity === true;

const normalizeOrderNumbers = (value: unknown): number[] => {
  if (!Array.isArray(value)) {
    return [];
  }
  const unique = new Set<number>();
  value.forEach((entry) => {
    const parsed = toPositiveInteger(entry);
    if (parsed !== undefined) {
      unique.add(parsed);
    }
  });
  return Array.from(unique).sort((a, b) => a - b);
};

const buildControlItemReportKey = (taskId?: number, orderNumber?: number): string | undefined => {
  if (!taskId || !orderNumber) {
    return undefined;
  }
  return `${taskId}:${orderNumber}`;
};

const normalizeDownloadFilenamePart = (value: unknown): string | null => {
  if (value == null) {
    return null;
  }
  const normalized = String(value)
    .trim()
    .replace(/Ç/g, 'C')
    .replace(/Ğ/g, 'G')
    .replace(/İ/g, 'I')
    .replace(/Ö/g, 'O')
    .replace(/Ş/g, 'S')
    .replace(/Ü/g, 'U')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ş/g, 's')
    .replace(/ü/g, 'u')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^[-._]+|[-._]+$/g, '');
  return normalized || null;
};

const formatReportDownloadTimestamp = (date = new Date()): string => {
  const pad = (value: number) => String(value).padStart(2, '0');
  return [
    date.getFullYear(),
    pad(date.getMonth() + 1),
    pad(date.getDate()),
    pad(date.getHours()),
    pad(date.getMinutes()),
    pad(date.getSeconds()),
  ].join('');
};

const decodeFilenameComponent = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const extractReportFilenameFromUrl = (url?: string | null): string | null => {
  const trimmedUrl = toOptionalString(url);
  if (!trimmedUrl) {
    return null;
  }
  try {
    const parsed = new URL(trimmedUrl, window.location.origin);
    const filename = decodeFilenameComponent(parsed.pathname.split('/').filter(Boolean).pop() ?? '');
    return filename.toLowerCase().endsWith('.pdf') ? normalizeDownloadFilenamePart(filename)?.replace(/-pdf$/i, '.pdf') ?? null : null;
  } catch {
    const filename = decodeFilenameComponent(trimmedUrl.split('?')[0].split('#')[0].split('/').filter(Boolean).pop() ?? '');
    return filename.toLowerCase().endsWith('.pdf') ? normalizeDownloadFilenamePart(filename)?.replace(/-pdf$/i, '.pdf') ?? null : null;
  }
};

const buildReportDownloadFilename = (attributeName: string, item: ControlItem): string => {
  const existingFilename = extractReportFilenameFromUrl(item.reportUrl);
  if (existingFilename) {
    return existingFilename;
  }
  const parts = [
    normalizeDownloadFilenamePart(attributeName),
    normalizeDownloadFilenamePart(item.reportNumber || `sira-${item.displayOrder}`),
    formatReportDownloadTimestamp(),
  ].filter((part): part is string => Boolean(part));
  return `${parts.join('-') || 'rapor'}.pdf`;
};

const buildBulkReportDownloadFilename = (attributeName: string, groupIndex: number): string => {
  const parts = [
    normalizeDownloadFilenamePart(attributeName),
    normalizeDownloadFilenamePart(`toplu-giris-${groupIndex + 1}`),
    formatReportDownloadTimestamp(),
  ].filter((part): part is string => Boolean(part));
  return `${parts.join('-') || 'toplu-rapor'}.pdf`;
};

const resolveTaskAttributeId = (task: any): number | undefined =>
  toPositiveNumber(task?.attributeId ?? task?.attribute?.id ?? task?.attribute);

const resolveAttributeToolId = (task: any, attribute: any): number | undefined =>
  toPositiveNumber(
    task?.toolId ??
    task?.tool?.id ??
    task?.attribute?.toolId ??
    task?.attribute?.tool?.id ??
    attribute?.toolId ??
    attribute?.tool?.id,
  );

const resolveAttributeToolName = (
  task: any,
  attribute: any,
  toolNameById: Map<number, string>,
): string | undefined => {
  const directName = (
    task?.toolName ??
    task?.tool?.name ??
    task?.attribute?.toolName ??
    task?.attribute?.tool?.name ??
    attribute?.toolName ??
    attribute?.tool?.name ??
    ''
  ).toString().trim();
  if (directName) {
    return directName;
  }

  const toolId = resolveAttributeToolId(task, attribute);
  return toolId ? toolNameById.get(toolId) : undefined;
};

const resolveTaskAccountId = (task: any): number | undefined =>
  toPositiveNumber(
    task?.accountId ??
    task?.account?.id ??
    task?.inspectorId ??
    task?.inspector?.id ??
    task?.inspector?.accountId ??
    task?.inspector?.account?.id,
  );

const resolveAppointmentAccountId = (appointment: any): number | undefined =>
  toPositiveNumber(
    appointment?.accountId ??
    appointment?.account?.id ??
    appointment?.inspectorId ??
    appointment?.inspector?.id ??
    appointment?.inspector?.accountId ??
    appointment?.inspector?.account?.id,
  );

const resolveInspectorDisplayName = (source: any): string | undefined => {
  const firstName = (
    source?.account?.firstName ??
    source?.inspector?.account?.firstName ??
    source?.inspector?.firstName ??
    source?.firstName ??
    ''
  ).toString().trim();
  const lastName = (
    source?.account?.lastName ??
    source?.inspector?.account?.lastName ??
    source?.inspector?.lastName ??
    source?.lastName ??
    ''
  ).toString().trim();
  const fullName = `${firstName} ${lastName}`.trim();
  if (fullName) {
    return fullName;
  }

  const directName = (
    source?.account?.fullName ??
    source?.inspector?.account?.fullName ??
    source?.inspector?.fullName ??
    source?.fullName ??
    source?.name ??
    ''
  ).toString().trim();
  return directName || undefined;
};

const isExtraTask = (task: any): boolean =>
  task?.isExtra === true || task?.extra === true || task?.is_extra === true;

const isCorrectionTask = (task: any): boolean =>
  task?.isCorrection === true || task?.correction === true || task?.is_correction === true;

const isTruthyValue = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

const normalizeCorrectionCaseType = (value: unknown): CorrectionCaseType | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const normalized = value.trim().toUpperCase();
  return normalized === 'FOLLOW_UP' || normalized === 'CORRECTION'
    ? normalized
    : undefined;
};

const resolveCorrectionCaseType = (task: any): CorrectionCaseType | undefined => {
  const directType = normalizeCorrectionCaseType(
    task?.caseType ??
      task?.case_type ??
      task?.correctionCaseType ??
      task?.correction_case_type,
  );
  if (directType) {
    return directType;
  }
  if (
    toPositiveNumber(task?.followUpCaseId ?? task?.follow_up_case_id) ||
    isTruthyValue(task?.isFollowUp ?? task?.followUp ?? task?.is_follow_up)
  ) {
    return 'FOLLOW_UP';
  }
  return isCorrectionTask(task) ? 'CORRECTION' : undefined;
};

const correctionCaseTypeLabelMap: Record<CorrectionCaseType, { label: string; color: string }> = {
  CORRECTION: { label: 'DÜZELTME KONTROLÜ', color: 'purple' },
  FOLLOW_UP: { label: 'TAKİP KONTROLÜ', color: 'geekblue' },
};

const resolveCorrectionCaseTypeLabel = (caseType?: CorrectionCaseType) =>
  correctionCaseTypeLabelMap[caseType ?? 'CORRECTION'];

const resolveResponseProgress = (savedResponseCount: number, qtyCount: number) => {
  if (qtyCount <= 0) {
    return {
      color: 'default',
      label: 'Henüz başlanmadı',
      text: '-',
      tooltip: 'Henüz başlanmadı - rapor adedi bulunmuyor',
    };
  }
  const safeSavedCount = Math.min(Math.max(savedResponseCount, 0), qtyCount);
  const text = `${safeSavedCount}/${qtyCount}`;
  if (safeSavedCount === 0) {
    return {
      color: 'red',
      label: 'Henüz başlanmadı',
      text,
      tooltip: `Henüz başlanmadı - ${text} rapor kaydedildi`,
    };
  }
  if (safeSavedCount >= qtyCount) {
    return {
      color: 'green',
      label: 'Tamamlandı',
      text,
      tooltip: `Tamamlandı - ${text} rapor kaydedildi`,
    };
  }
  return {
    color: 'orange',
    label: 'Başladı',
    text,
    tooltip: `Başladı - ${text} rapor kaydedildi`,
  };
};

const resolveReportFormatAvailability = (status?: ReportFormatStatus) => {
  if (!status?.hasReportFormat) {
    return {
      canUse: false,
      label: 'Rapor formatı yok',
      color: 'red',
      tooltip: 'Bu parametre için rapor formatı yok.',
    };
  }
  if (!status.isActive && !status.isTested) {
    return {
      canUse: false,
      label: 'Aktif değil veya test edilmedi',
      color: 'red',
      tooltip: 'Rapor formatı aktif değil veya test edilmedi.',
    };
  }
  if (!status.isActive) {
    return {
      canUse: false,
      label: 'Rapor formatı aktif değil',
      color: 'red',
      tooltip: 'Rapor formatı aktif değil.',
    };
  }
  if (!status.isTested) {
    return {
      canUse: false,
      label: 'Rapor formatı test edilmedi',
      color: 'orange',
      tooltip: 'Rapor formatı test edilmedi.',
    };
  }
  return {
    canUse: true,
    label: 'Rapor formatı hazır',
    color: 'green',
    tooltip: 'Rapor formatı aktif ve test edildi.',
  };
};

const InspectorPlanTasksCard: React.FC<InspectorPlanTasksCardProps> = ({
  tasks,
  archiveTasks = tasks,
  attributeNameById,
  reportFormatStatusByAttributeId,
  proposalId,
  className,
  title = 'Görevler',
  attributes = EMPTY_ITEMS,
  tools = EMPTY_ITEMS,
  appointments = EMPTY_ITEMS,
  inspectors = EMPTY_ITEMS,
  currentAccountId,
  isInspector = false,
  canSelectAllPlanInspectors = false,
  onExtraTaskCreated,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [activeTask, setActiveTask] = useState<ResolvedTask | null>(null);
  const [bulkReportCountDraft, setBulkReportCountDraft] = useState<number | null>(null);
  const [bulkGroups, setBulkGroups] = useState<ControlTaskBulkGroup[]>([]);
  const [bulkGroupsLoading, setBulkGroupsLoading] = useState<boolean>(false);
  const [bulkCapacity, setBulkCapacity] = useState<ControlTaskBulkCapacity | null>(null);
  const [bulkCapacityLoading, setBulkCapacityLoading] = useState<boolean>(false);
  const [bulkCapacityError, setBulkCapacityError] = useState<string | null>(null);
  const [bulkProgressByAttributeId, setBulkProgressByAttributeId] = useState<Record<number, BulkProgressSummary>>({});
  const [extraModalOpen, setExtraModalOpen] = useState(false);
  const [extraMode, setExtraMode] = useState<ExtraTaskMode>('add');
  const [extraTarget, setExtraTarget] = useState<ResolvedTask | null>(null);
  const [savingExtra, setSavingExtra] = useState(false);
  const [uploadItemKey, setUploadItemKey] = useState<string | null>(null);
  const [signedReportFileList, setSignedReportFileList] = useState<UploadFile[]>([]);
  const [uploadingSignedReport, setUploadingSignedReport] = useState(false);
  const [downloadingAllReports, setDownloadingAllReports] = useState(false);
  const [archiveSelectionDrawerOpen, setArchiveSelectionDrawerOpen] = useState(false);
  const [selectedArchiveAccountIds, setSelectedArchiveAccountIds] = useState<number[]>([]);
  const [downloadingBulkGroupId, setDownloadingBulkGroupId] = useState<number | null>(null);
  const [deletingBulkGroupId, setDeletingBulkGroupId] = useState<number | null>(null);
  const [uploadedReportOverrides, setUploadedReportOverrides] = useState<Record<string, UploadedReportOverride>>({});
  const normalizedProposalId = toPositiveNumber(proposalId);

  const plannedInspectorOptions = useMemo(() => {
    const map = new Map<number, string>();
    const inspectorNameByAccountId = new Map<number, string>();

    inspectors.forEach((inspector: any) => {
      const accountId = resolveTaskAccountId(inspector);
      const label = resolveInspectorDisplayName(inspector);
      if (accountId && label) {
        inspectorNameByAccountId.set(accountId, label);
      }
    });

    appointments.forEach((appointment: any) => {
      const accountId = resolveAppointmentAccountId(appointment);
      if (!accountId || map.has(accountId)) {
        return;
      }
      map.set(
        accountId,
        resolveInspectorDisplayName(appointment) ?? inspectorNameByAccountId.get(accountId) ?? `P.K.M. #${accountId}`,
      );
    });
    archiveTasks.forEach((task: any) => {
      const accountId = resolveTaskAccountId(task);
      if (!accountId || map.has(accountId)) {
        return;
      }
      map.set(
        accountId,
        resolveInspectorDisplayName(task) ?? inspectorNameByAccountId.get(accountId) ?? `P.K.M. #${accountId}`,
      );
    });
    return Array.from(map.entries()).map(([value, label]) => ({ value, label }));
  }, [appointments, archiveTasks, inspectors]);

  const attributeById = useMemo(() => {
    const map = new Map<number, any>();
    attributes.forEach((attribute: any) => {
      const attributeId = toPositiveNumber(attribute?.id);
      if (attributeId) {
        map.set(attributeId, attribute);
      }
    });
    return map;
  }, [attributes]);

  const toolNameById = useMemo(() => {
    const map = new Map<number, string>();
    tools.forEach((tool: any) => {
      const toolId = toPositiveNumber(tool?.id);
      const name = (tool?.name ?? tool?.toolName ?? '').toString().trim();
      if (toolId && name) {
        map.set(toolId, name);
      }
    });
    return map;
  }, [tools]);

  const resolvedTasks = useMemo(() => {
    if (!Array.isArray(tasks)) {
      return [];
    }
    const grouped = new Map<number, ResolvedTask>();
    tasks.forEach((task: any) => {
      const attributeId = resolveTaskAttributeId(task);
      if (!attributeId) {
        return;
      }
      const taskId = toPositiveNumber(task?.id);
      const qtyCount = toPositiveInteger(task?.qty) ?? 0;
      const savedOrderNumbers = normalizeOrderNumbers(task?.responseOrderNumbers);
      const responseReports = Array.isArray(task?.responseReports) ? task.responseReports : [];
      const assumeAllSaved = Boolean(task?.hasResponse) && savedOrderNumbers.length === 0;
      const attribute = attributeById.get(attributeId);
      const componentId = resolveAttributeToolId(task, attribute);
      const componentName = resolveAttributeToolName(task, attribute, toolNameById);
      const bulkReportEntry = isTruthyValue(
        task?.bulkReportEntry ??
        task?.bulk_report_entry ??
        task?.attribute?.bulkReportEntry ??
        task?.attribute?.bulk_report_entry ??
        attribute?.bulkReportEntry ??
        attribute?.bulk_report_entry,
      );
      const status =
        typeof task?.status === 'string' && task.status.trim().length ? task.status.trim() : undefined;
      const existing = grouped.get(attributeId) ?? {
        key: String(attributeId),
        attributeName: attributeNameById.get(attributeId) ?? `Parametre #${attributeId}`,
        qtyCount: 0,
        statuses: [],
        attributeId,
        componentId,
        componentName,
        bulkReportEntry,
        controlItems: [],
        savedResponseCount: 0,
        hasSavedResponse: false,
        hasExtra: false,
        correctionCaseTypes: [],
      };
      if (!existing.componentId && componentId) {
        existing.componentId = componentId;
      }
      if (!existing.componentName && componentName) {
        existing.componentName = componentName;
      }
      if (status && !existing.statuses.includes(status)) {
        existing.statuses.push(status);
      }
      const extra = isExtraTask(task);
      const correctionCaseType = resolveCorrectionCaseType(task);
      const correction = isCorrectionTask(task) || correctionCaseType !== undefined;
      existing.hasExtra = existing.hasExtra || extra;
      if (correction) {
        const resolvedCaseType = correctionCaseType ?? 'CORRECTION';
        if (!existing.correctionCaseTypes.includes(resolvedCaseType)) {
          existing.correctionCaseTypes.push(resolvedCaseType);
        }
      }
      existing.bulkReportEntry = existing.bulkReportEntry || bulkReportEntry;
      for (let index = 0; index < qtyCount; index += 1) {
        const orderNumber = index + 1;
        const hasSavedResponse = assumeAllSaved || savedOrderNumbers.includes(orderNumber);
        const responseReport = responseReports.find((report: any) => toPositiveInteger(report?.orderNo) === orderNumber);
        const reportKey = buildControlItemReportKey(taskId, orderNumber);
        const reportOverride = reportKey ? uploadedReportOverrides[reportKey] : undefined;
        existing.controlItems.push({
          key: `${taskId ?? 'task'}-${orderNumber}-${existing.controlItems.length}`,
          displayOrder: existing.controlItems.length + 1,
          orderNumber,
          taskId,
          attributeId,
          rawTask: task,
          hasSavedResponse,
          isExtra: extra,
          isCorrection: correction,
          correctionCaseType: correction ? correctionCaseType ?? 'CORRECTION' : undefined,
          correctionCaseId: toPositiveNumber(task?.correctionCaseId),
          correctionStatus: typeof task?.correctionStatus === 'string' ? task.correctionStatus : undefined,
          sourceReportNumber: typeof task?.sourceReportNumber === 'string' ? task.sourceReportNumber : undefined,
          reportUrl: resolveReportUrl(responseReport ?? task),
          signedReportUrl: reportOverride?.signedReportUrl ?? resolveSignedReportUrl(responseReport ?? task),
          reportApproved: reportOverride?.reportApproved ?? resolveReportApproved(responseReport ?? task),
          reportNumber: reportOverride?.reportNumber ?? resolveReportNumber(responseReport ?? task),
          hasNonconformity: resolveHasNonconformity(responseReport),
        });
        if (hasSavedResponse) {
          existing.savedResponseCount += 1;
        }
        existing.hasSavedResponse = existing.hasSavedResponse || hasSavedResponse;
      }
      existing.qtyCount += qtyCount;
      grouped.set(attributeId, existing);
    });
    return Array.from(grouped.values()).sort((a, b) => a.attributeName.localeCompare(b.attributeName, 'tr'));
  }, [tasks, attributeNameById, uploadedReportOverrides, attributeById, toolNameById]);

  const taskComponentGroups = useMemo(() => {
    const groupMap = new Map<string, TaskComponentGroup>();
    const ungrouped: ResolvedTask[] = [];

    resolvedTasks.forEach((task) => {
      const componentName = task.componentName?.trim();
      if (!componentName) {
        ungrouped.push(task);
        return;
      }

      const key = task.componentId ? `component-${task.componentId}` : `component-${componentName.toLocaleLowerCase('tr')}`;
      const existing = groupMap.get(key);
      if (existing) {
        existing.tasks.push(task);
      } else {
        groupMap.set(key, {
          key,
          componentName,
          tasks: [task],
        });
      }
    });

    const groups = Array.from(groupMap.values()).sort((a, b) => a.componentName.localeCompare(b.componentName, 'tr'));
    groups.forEach((group) => {
      group.tasks.sort((a, b) => a.attributeName.localeCompare(b.attributeName, 'tr'));
    });
    ungrouped.sort((a, b) => a.attributeName.localeCompare(b.attributeName, 'tr'));

    return { groups, ungrouped };
  }, [resolvedTasks]);

  const existingAttributeIds = useMemo(
    () => new Set(resolvedTasks.map((task) => task.attributeId).filter((id): id is number => id !== undefined)),
    [resolvedTasks],
  );

  useEffect(() => {
    if (!normalizedProposalId) {
      setBulkProgressByAttributeId({});
      return;
    }
    const bulkTasks = resolvedTasks
      .filter((task) => task.bulkReportEntry)
      .map((task) => {
        const firstItem = task.controlItems[0];
        if (!task.attributeId || !firstItem?.taskId) {
          return null;
        }
        return {
          attributeId: task.attributeId,
          taskId: firstItem.taskId,
        };
      })
      .filter((item): item is { attributeId: number; taskId: number } => Boolean(item));

    if (!bulkTasks.length) {
      setBulkProgressByAttributeId({});
      return;
    }

    let ignore = false;
    const progressRequests: Array<Promise<{ attributeId: number; groups: ControlTaskBulkGroup[] }>> = bulkTasks.map((task) =>
      fetchControlTaskBulkGroups(normalizedProposalId, task.taskId, task.attributeId)
        .then((groups) => ({
          attributeId: task.attributeId,
          groups: Array.isArray(groups) ? groups : [],
        }))
        .catch((error) => {
          console.error('InspectorPlanTasksCard: bulk progress could not be loaded', error);
          return {
            attributeId: task.attributeId,
            groups: [] as ControlTaskBulkGroup[],
          };
        }),
    );

    Promise.all(progressRequests).then((results) => {
      if (ignore) {
        return;
      }
      const next: Record<number, BulkProgressSummary> = {};
      results.forEach(({ attributeId, groups }) => {
        next[attributeId] = resolveBulkProgressSummary(groups);
      });
      setBulkProgressByAttributeId(next);
    });

    return () => {
      ignore = true;
    };
  }, [normalizedProposalId, resolvedTasks]);

  const addableAttributeOptions = useMemo(
    () =>
      attributes
        .map((attribute: any) => {
          const value = toPositiveNumber(attribute?.id);
          if (!value || existingAttributeIds.has(value)) {
            return null;
          }
          const label = (attribute?.name || `Parametre #${value}`).toString();
          const availability = resolveReportFormatAvailability(reportFormatStatusByAttributeId?.get(value));
          return {
            value,
            label,
            disabled: !availability.canUse,
            title: availability.canUse ? undefined : availability.label,
          };
        })
        .filter((option): option is { value: number; label: string; disabled: boolean; title: string | undefined } => Boolean(option))
        .sort((a, b) => a.label.localeCompare(b.label, 'tr')),
    [attributes, existingAttributeIds, reportFormatStatusByAttributeId],
  );

  const hasAddableReportFormatOption = useMemo(
    () => addableAttributeOptions.some((option) => !option.disabled),
    [addableAttributeOptions],
  );

  const canDownloadPlanReports = Boolean(normalizedProposalId && plannedInspectorOptions.length > 0);

  const resolveDefaultAccountId = (target?: ResolvedTask | null) => {
    if (isInspector && currentAccountId) {
      return currentAccountId;
    }
    const taskAccountId = target?.controlItems
      .map((item) => resolveTaskAccountId(item.rawTask))
      .find((accountId) => accountId !== undefined);
    return taskAccountId ?? (plannedInspectorOptions.length === 1 ? plannedInspectorOptions[0].value : undefined);
  };

  const openExtraModal = (mode: ExtraTaskMode, target?: ResolvedTask) => {
    const nextAccountId = resolveDefaultAccountId(target);
    setExtraMode(mode);
    setExtraTarget(target ?? null);
    form.setFieldsValue({
      accountId: nextAccountId,
      attributeId: target?.attributeId,
      qty: 1,
    });
    setExtraModalOpen(true);
  };

  const closeExtraModal = () => {
    setExtraModalOpen(false);
    setExtraTarget(null);
    form.resetFields();
  };

  const handleSaveExtraTask = async () => {
    if (!normalizedProposalId) {
      message.error('Plan bilgisi bulunamadı.');
      return;
    }
    try {
      const values = await form.validateFields();
      const accountId = isInspector && currentAccountId ? currentAccountId : toPositiveNumber(values.accountId);
      const attributeId = extraMode === 'increase' ? extraTarget?.attributeId : toPositiveNumber(values.attributeId);
      const qty = toPositiveInteger(values.qty);
      if (!accountId) {
        message.error('Uzman seçiniz.');
        return;
      }
      if (!attributeId) {
        message.error('Parametre seçiniz.');
        return;
      }
      const availability = resolveReportFormatAvailability(reportFormatStatusByAttributeId?.get(attributeId));
      if (!availability.canUse) {
        message.error(availability.label);
        return;
      }
      if (!qty) {
        message.error('Adet en az 1 olmalıdır.');
        return;
      }
      setSavingExtra(true);
      await createExtraInspectionTask({
        proposalId: normalizedProposalId,
        accountId,
        attributeId,
        qty,
      });
      message.success(extraMode === 'increase' ? 'Adet güncellendi.' : 'Yeni parametre eklendi.');
      closeExtraModal();
      onExtraTaskCreated?.();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      message.error(error?.message || 'Yeni parametre kaydedilemedi.');
    } finally {
      setSavingExtra(false);
    }
  };

  const handleOpenTask = (task: ResolvedTask) => {
    setActiveTask(task);
    setBulkReportCountDraft(null);
    setBulkGroups([]);
    setBulkCapacity(null);
    setBulkCapacityError(null);
    setDrawerVisible(true);
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setActiveTask(null);
    setBulkReportCountDraft(null);
    setBulkGroups([]);
    setBulkCapacity(null);
    setBulkCapacityError(null);
    setUploadItemKey(null);
    setSignedReportFileList([]);
  };

  useEffect(() => {
    if (!drawerVisible || !activeTask?.bulkReportEntry || !normalizedProposalId) {
      return;
    }
    const firstItem = activeTask.controlItems[0];
    if (!firstItem?.taskId || !firstItem?.attributeId) {
      return;
    }
    let ignore = false;
    setBulkGroupsLoading(true);
    setBulkCapacityLoading(true);
    setBulkCapacityError(null);
    fetchControlTaskBulkGroups(normalizedProposalId, firstItem.taskId, firstItem.attributeId)
      .then((groups) => {
        if (!ignore) {
          setBulkGroups(Array.isArray(groups) ? groups : []);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error('InspectorPlanTasksCard: bulk groups could not be loaded', error);
          message.error('Toplu girişler yüklenemedi.');
          setBulkGroups([]);
        }
      })
      .finally(() => {
        if (!ignore) {
          setBulkGroupsLoading(false);
        }
      });
    fetchControlTaskBulkCapacity(normalizedProposalId, firstItem.taskId, firstItem.attributeId)
      .then((capacity) => {
        if (!ignore) {
          setBulkCapacity(capacity ?? null);
        }
      })
      .catch((error) => {
        if (!ignore) {
          console.error('InspectorPlanTasksCard: bulk capacity could not be loaded', error);
          setBulkCapacity(null);
          setBulkCapacityError('Toplu rapor kotası yüklenemedi. Lütfen tekrar deneyiniz.');
        }
      })
      .finally(() => {
        if (!ignore) {
          setBulkCapacityLoading(false);
        }
      });
    return () => {
      ignore = true;
    };
  }, [drawerVisible, activeTask, normalizedProposalId]);

  const bulkReportCountLimit = useMemo(() => {
    if (!activeTask?.bulkReportEntry || !bulkCapacity) {
      return undefined;
    }
    const value = Number(bulkCapacity.maximumReportCount);
    return Number.isFinite(value) && value >= 0 ? Math.floor(value) : undefined;
  }, [activeTask, bulkCapacity]);
  const hasBulkCapacity = bulkReportCountLimit !== undefined && !bulkCapacityLoading && !bulkCapacityError;

  useEffect(() => {
    if (!drawerVisible || !activeTask?.bulkReportEntry || bulkReportCountLimit === undefined) {
      return;
    }
    setBulkReportCountDraft((prev) => {
      if (bulkReportCountLimit <= 0) {
        return null;
      }
      if (prev === null) {
        return null;
      }
      return Math.min(Math.max(Math.floor(Number(prev) || 1), 1), bulkReportCountLimit);
    });
  }, [drawerVisible, activeTask, bulkReportCountLimit]);

  const handleStartControl = (item: ControlItem) => {
    if (
      normalizedProposalId === undefined ||
      item.attributeId === undefined ||
      item.taskId === undefined
    ) {
      message.error('Kontrol başlatmak için gerekli bilgiler eksik.');
      return;
    }
    const availability = resolveReportFormatAvailability(reportFormatStatusByAttributeId?.get(item.attributeId));
    if (!availability.canUse) {
      message.error(availability.label);
      return;
    }
    const correctionQuery = item.isCorrection && item.correctionCaseId
      ? `?correctionCaseId=${item.correctionCaseId}`
      : '';
    const path = `/control/${normalizedProposalId}/taskId/${item.taskId}/attribute/${item.attributeId}/order/${item.orderNumber}${correctionQuery}`;
    navigate(path, {
      state: {
        task: item.rawTask,
        attributeName: activeTask?.attributeName,
        qty: activeTask?.qtyCount,
        correctionCaseId: item.correctionCaseId,
        correctionCaseType: item.correctionCaseType,
      },
    });
    handleCloseDrawer();
  };

  const handleStartBulkControl = () => {
    if (!activeTask) {
      return;
    }
    const firstItem = activeTask.controlItems[0];
    if (
      normalizedProposalId === undefined ||
      firstItem?.attributeId === undefined ||
      firstItem?.taskId === undefined
    ) {
      message.error('Toplu giriş için gerekli kontrol bilgileri eksik.');
      return;
    }
    const availability = resolveReportFormatAvailability(reportFormatStatusByAttributeId?.get(firstItem.attributeId));
    if (!availability.canUse) {
      message.error(availability.label);
      return;
    }
    const count = bulkReportCountDraft === null ? 0 : Math.max(Math.floor(Number(bulkReportCountDraft) || 0), 1);
    if (count <= 0) {
      message.error('Rapor sayısı giriniz.');
      return;
    }
    if (!hasBulkCapacity || (bulkReportCountLimit ?? 0) <= 0 || count > (bulkReportCountLimit ?? 0)) {
      message.error('Görev sayısı aşılamaz.');
      return;
    }
    const path = `/control/${normalizedProposalId}/taskId/${firstItem.taskId}/attribute/${firstItem.attributeId}/order/1?bulkReportCount=${count}`;
    navigate(path, {
      state: {
        task: firstItem.rawTask,
        attributeName: activeTask.attributeName,
        qty: activeTask.qtyCount,
        bulkReportCount: count,
      },
    });
    handleCloseDrawer();
  };

  const handleEditBulkControl = (group: ControlTaskBulkGroup) => {
    if (!activeTask) {
      return;
    }
    const firstItem = activeTask.controlItems[0];
    const bulkGroupId = toPositiveNumber(group?.id);
    if (
      normalizedProposalId === undefined ||
      firstItem?.attributeId === undefined ||
      firstItem?.taskId === undefined ||
      bulkGroupId === undefined
    ) {
      message.error('Toplu girişi düzenlemek için gerekli bilgiler eksik.');
      return;
    }
    const availability = resolveReportFormatAvailability(reportFormatStatusByAttributeId?.get(firstItem.attributeId));
    if (!availability.canUse) {
      message.error(availability.label);
      return;
    }
    const count = Math.max(
      Math.floor(Number(group.reportCount ?? group.savedResponseCount ?? bulkReportCountDraft) || 1),
      1,
    );
    const path = `/control/${normalizedProposalId}/taskId/${firstItem.taskId}/attribute/${firstItem.attributeId}/order/1?bulkGroupId=${bulkGroupId}&bulkReportCount=${count}`;
    navigate(path, {
      state: {
        task: firstItem.rawTask,
        attributeName: activeTask.attributeName,
        qty: activeTask.qtyCount,
        bulkGroupId,
        bulkReportCount: count,
      },
    });
    handleCloseDrawer();
  };

  const updateBulkProgress = (attributeId: number, groups: ControlTaskBulkGroup[]) => {
    setBulkProgressByAttributeId((current) => ({
      ...current,
      [attributeId]: resolveBulkProgressSummary(groups),
    }));
  };

  const handleDeleteBulkControl = async (group: ControlTaskBulkGroup) => {
    if (!activeTask) {
      return;
    }
    const firstItem = activeTask.controlItems[0];
    const bulkGroupId = toPositiveNumber(group?.id);
    if (
      normalizedProposalId === undefined ||
      firstItem?.attributeId === undefined ||
      firstItem?.taskId === undefined ||
      bulkGroupId === undefined
    ) {
      message.error('Toplu girişi silmek için gerekli bilgiler eksik.');
      return;
    }

    try {
      setDeletingBulkGroupId(bulkGroupId);
      await deleteControlTaskBulkGroup(
        normalizedProposalId,
        firstItem.taskId,
        firstItem.attributeId,
        bulkGroupId,
      );

      const remainingGroups = bulkGroups.filter((item) => toPositiveNumber(item.id) !== bulkGroupId);
      setBulkGroups(remainingGroups);
      updateBulkProgress(firstItem.attributeId, remainingGroups);
      message.success('Toplu giriş silindi.');

      setBulkGroupsLoading(true);
      setBulkCapacityLoading(true);
      try {
        const refreshedGroups = await fetchControlTaskBulkGroups(
          normalizedProposalId,
          firstItem.taskId,
          firstItem.attributeId,
        );
        const nextGroups = Array.isArray(refreshedGroups) ? refreshedGroups : [];
        setBulkGroups(nextGroups);
        updateBulkProgress(firstItem.attributeId, nextGroups);
        const refreshedCapacity = await fetchControlTaskBulkCapacity(
          normalizedProposalId,
          firstItem.taskId,
          firstItem.attributeId,
        );
        setBulkCapacity(refreshedCapacity ?? null);
        setBulkCapacityError(null);
      } catch (refreshError) {
        console.error('InspectorPlanTasksCard: bulk groups could not be refreshed after deletion', refreshError);
        setBulkCapacity(null);
        setBulkCapacityError('Toplu rapor kotası yüklenemedi. Lütfen tekrar deneyiniz.');
      } finally {
        setBulkGroupsLoading(false);
        setBulkCapacityLoading(false);
      }
    } catch (error) {
      console.error('Toplu giriş silme hatası:', error);
      message.error('Toplu giriş silinemedi.');
    } finally {
      setDeletingBulkGroupId(null);
    }
  };

  const handleDownloadBulkControl = async (group: ControlTaskBulkGroup, groupIndex: number) => {
    if (!activeTask) {
      return;
    }
    const firstItem = activeTask.controlItems[0];
    const bulkGroupId = toPositiveNumber(group?.id);
    const savedResponseCount = Math.max(Math.floor(Number(group.savedResponseCount) || 0), 0);
    if (
      normalizedProposalId === undefined ||
      firstItem?.attributeId === undefined ||
      firstItem?.taskId === undefined ||
      bulkGroupId === undefined
    ) {
      message.error('Toplu raporu indirmek için gerekli bilgiler eksik.');
      return;
    }
    if (savedResponseCount <= 0) {
      message.warning('İndirilecek toplu rapor kaydı bulunamadı.');
      return;
    }

    try {
      setDownloadingBulkGroupId(bulkGroupId);
      const published = await publishBulkControlTaskReport(
        normalizedProposalId,
        firstItem.taskId,
        firstItem.attributeId,
        1,
        bulkGroupId,
      );
      const reportUrl = published.url || published.reportUrl;
      if (!reportUrl) {
        throw new Error('Yayınlanmış toplu rapor bağlantısı bulunamadı.');
      }
      saveAs(
        reportUrl,
        published.filename || buildBulkReportDownloadFilename(activeTask.attributeName, groupIndex),
      );
      message.success('Toplu rapor indirildi.');
    } catch (error) {
      console.error('Toplu rapor indirme hatası:', error);
      message.error('Toplu rapor indirilemedi.');
    } finally {
      setDownloadingBulkGroupId(null);
    }
  };

  const handleDownloadReport = (item: ControlItem) => {
    openReportUrl(item.reportUrl);
  };

  const openReportUrl = (url?: string | null) => {
    if (!url) {
      message.warning('Yüklü rapor bulunamadı.');
      return;
    }
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (!newWindow) {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
    }
  };

  const downloadReportUrl = (url?: string | null) => {
    if (!url) {
      message.warning('İndirilecek rapor bulunamadı.');
      return;
    }
    openReportUrl(url);
  };

  const renderIconButton = (
    tooltip: string,
    ariaLabel: string,
    icon: React.ReactNode,
    onClick: () => void,
    disabled = false,
    type: 'default' | 'primary' = 'default',
  ) => (
    <Tooltip title={tooltip}>
      <span>
        <Button
          aria-label={ariaLabel}
          icon={icon}
          type={type}
          onClick={onClick}
          disabled={disabled}
        />
      </span>
    </Tooltip>
  );

  const renderNonconformityIcon = (key: string) => (
    <Tooltip key={key} title='Uygunsuz seçim var'>
      <span aria-label='Uygunsuz seçim var' role='img'>
        <WarningOutlined style={{ color: '#ff4d4f' }} />
      </span>
    </Tooltip>
  );

  const validateSignedReportFile = (file: File | UploadFile): boolean => {
    const type = 'type' in file ? file.type : undefined;
    const name = 'name' in file ? file.name : undefined;
    const isPdf = type === 'application/pdf' || Boolean(name && name.toLowerCase().endsWith('.pdf'));
    if (!isPdf) {
      message.error('Sadece PDF dosyası yükleyiniz.');
      return false;
    }
    return true;
  };

  const createUploadFile = (file: File): UploadFile => ({
    uid: `${Date.now()}-${file.name}`,
    name: file.name,
    status: 'done',
    originFileObj: file as any,
  });

  const handleSignedReportPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    if (uploadingSignedReport) {
      return;
    }
    const files = Array.from(event.clipboardData?.files ?? []);
    const pdf = files.find(validateSignedReportFile);
    if (!pdf) {
      return;
    }
    event.preventDefault();
    setSignedReportFileList([createUploadFile(pdf)]);
  };

  const handleOpenUploadDrawer = (item: ControlItem) => {
    setUploadItemKey(item.key);
    setSignedReportFileList([]);
  };

  const handleCloseUploadDrawer = () => {
    setUploadItemKey(null);
    setSignedReportFileList([]);
  };

  const drawerItems = useMemo(() => activeTask?.controlItems ?? [], [activeTask]);

  const downloadableReportItems = useMemo(
    () => drawerItems.filter((item) => item.hasSavedResponse),
    [drawerItems],
  );

  const canDownloadAllReports = Boolean(
    drawerVisible &&
    activeTask &&
    !activeTask.bulkReportEntry &&
    downloadableReportItems.length,
  );

  const emptyDownloadSummary = (): ReportDownloadSummary => ({
    requested: 0,
    success: 0,
    failed: 0,
  });

  const showDownloadSummaryMessage = (summary: ReportDownloadSummary) => {
    if (summary.success > 0) {
      message.success('Raporlar indirildi.');
    }
    if (summary.failed > 0) {
      message.warning('Raporlar indirilemedi.');
    }
    if (summary.requested <= 0) {
      message.warning('İndirilecek rapor bulunamadı.');
    }
  };

  const downloadStandardReportItem = async (task: ResolvedTask, item: ControlItem) => {
    if (
      normalizedProposalId === undefined ||
      item.taskId === undefined ||
      item.attributeId === undefined
    ) {
      throw new Error('Raporu indirmek için gerekli bilgiler eksik.');
    }

    const persisted = await fetchControlTaskAnswers(
      normalizedProposalId,
      item.taskId,
      item.attributeId,
      item.orderNumber,
    );
    const published = await publishControlTaskReport(
      normalizedProposalId,
      item.taskId,
      item.attributeId,
      item.orderNumber,
      persisted.answers ?? {},
      persisted.selectedControlAreaId ?? null,
      persisted.customControlAreaName ?? null,
      persisted.notes ?? {},
      persisted.inspectionNote ?? null,
    );
    const reportUrl = published.url || published.reportUrl;
    if (!reportUrl) {
      throw new Error('Yayınlanmış rapor bağlantısı bulunamadı.');
    }
    saveAs(
      reportUrl,
      published.filename || buildReportDownloadFilename(task.attributeName, item),
    );
  };

  const downloadStandardTaskReports = async (
    task: ResolvedTask,
    items: ControlItem[],
  ): Promise<ReportDownloadSummary> => {
    const downloadableItems = items.filter((item) => (
      item.hasSavedResponse &&
      item.taskId !== undefined &&
      item.attributeId !== undefined
    ));

    if (!downloadableItems.length) {
      return emptyDownloadSummary();
    }

    const results = await Promise.allSettled(
      downloadableItems.map((item) => downloadStandardReportItem(task, item)),
    );
    const failed = results.filter((result) => result.status === 'rejected').length;
    return {
      requested: results.length,
      success: results.length - failed,
      failed,
    };
  };

  const uploadItem = useMemo(
    () => drawerItems.find((item) => item.key === uploadItemKey) ?? null,
    [drawerItems, uploadItemKey],
  );

  const handleDownloadAllReports = async () => {
    if (!activeTask) {
      return;
    }
    if (!downloadableReportItems.length) {
      message.warning('İndirilecek rapor bulunamadı.');
      return;
    }
    if (normalizedProposalId === undefined) {
      message.error('Raporları indirmek için plan bilgisi eksik.');
      return;
    }

    try {
      setDownloadingAllReports(true);
      showDownloadSummaryMessage(await downloadStandardTaskReports(activeTask, downloadableReportItems));
    } catch (error) {
      console.error('Toplu rapor indirme hatası:', error);
      message.error('Raporlar indirilemedi.');
    } finally {
      setDownloadingAllReports(false);
    }
  };

  const openArchiveSelectionDrawer = () => {
    const plannedAccountIds = plannedInspectorOptions.map((option) => option.value);
    const defaultAccountIds = canSelectAllPlanInspectors
      ? plannedAccountIds
      : isInspector && currentAccountId && plannedAccountIds.includes(currentAccountId)
        ? [currentAccountId]
        : [];

    setSelectedArchiveAccountIds(defaultAccountIds);
    setArchiveSelectionDrawerOpen(true);
  };

  const handleDownloadAllPlanReports = async () => {
    if (!canDownloadPlanReports || normalizedProposalId === undefined) {
      message.warning('İndirilecek rapor bulunamadı.');
      return;
    }
    const plannedAccountIds = new Set(plannedInspectorOptions.map((option) => option.value));
    const accountIds = isInspector && currentAccountId && plannedAccountIds.has(currentAccountId)
      ? [currentAccountId]
      : Array.from(new Set(selectedArchiveAccountIds)).filter((accountId) => plannedAccountIds.has(accountId));
    if (!accountIds.length) {
      message.warning('En az bir P.K.M. uzmanı seçiniz.');
      return;
    }

    try {
      setArchiveSelectionDrawerOpen(false);
      setDownloadingAllReports(true);
      let archiveJob: ControlTaskReportArchiveJob = await requestControlTaskReportArchive(normalizedProposalId, accountIds);
      for (let attempt = 0; attempt < REPORT_ARCHIVE_MAX_POLL_ATTEMPTS; attempt += 1) {
        if (archiveJob.status === 'READY') {
          if (!archiveJob.downloadUrl) {
            throw new Error('ZIP rapor arşivinin indirme bağlantısı bulunamadı.');
          }
          saveAs(archiveJob.downloadUrl, archiveJob.filename || `kontrol-raporlari-${normalizedProposalId}.zip`);
          message.success('ZIP rapor arşivi hazırlandı ve indiriliyor.');
          return;
        }
        if (archiveJob.status === 'FAILED') {
          throw new Error(archiveJob.errorMessage || 'ZIP rapor arşivi hazırlanamadı.');
        }

        await new Promise<void>((resolve) => window.setTimeout(resolve, REPORT_ARCHIVE_POLL_INTERVAL_MS));
        archiveJob = await fetchControlTaskReportArchiveStatus(normalizedProposalId, archiveJob.archiveId);
      }
      throw new Error('ZIP rapor arşivi hazırlanırken zaman aşımı oluştu.');
    } catch (error) {
      console.error('Plan rapor arşivi indirme hatası:', error);
      message.error('ZIP rapor arşivi hazırlanamadı.');
    } finally {
      setDownloadingAllReports(false);
    }
  };

  const handleSaveSignedReport = async () => {
    if (
      normalizedProposalId === undefined ||
      !uploadItem ||
      uploadItem.taskId === undefined ||
      uploadItem.attributeId === undefined
    ) {
      message.error('Rapor yüklemek için gerekli bilgiler eksik.');
      return;
    }
    if (!signedReportFileList.length) {
      message.warning('PDF dosyası seçiniz.');
      return;
    }
    const uploadEntry = signedReportFileList[0];
    const fileObj: File | Blob | undefined = (uploadEntry as any)?.originFileObj ?? (uploadEntry as any);
    if (!fileObj) {
      message.error('Geçersiz dosya seçimi.');
      return;
    }
    try {
      setUploadingSignedReport(true);
      const updated = await uploadSignedControlTaskReport(
        normalizedProposalId,
        uploadItem.taskId,
        uploadItem.attributeId,
        uploadItem.orderNumber,
        fileObj,
      );
      const reportKey = buildControlItemReportKey(uploadItem.taskId, uploadItem.orderNumber);
      if (reportKey) {
        const nextReport = {
          signedReportUrl: resolveSignedReportUrl(updated),
          reportApproved: resolveReportApproved(updated) ?? true,
          reportNumber: resolveReportNumber(updated) ?? uploadItem.reportNumber ?? null,
        };
        setUploadedReportOverrides((prev) => ({
          ...prev,
          [reportKey]: nextReport,
        }));
        setActiveTask((prev) => {
          if (!prev) {
            return prev;
          }
          return {
            ...prev,
            controlItems: prev.controlItems.map((item) => (
              item.key === uploadItem.key ? { ...item, ...nextReport } : item
            )),
          };
        });
      }
      setSignedReportFileList([]);
      message.success('E-imzalı rapor yüklendi.');
    } catch (error) {
      console.error('E-imzalı rapor yükleme hatası:', error);
      message.error('Rapor yüklenemedi.');
    } finally {
      setUploadingSignedReport(false);
    }
  };

  const drawerTitle = activeTask ? `${activeTask.attributeName} Kontrol Listesi` : 'Kontrol';
  const canCreateExtra = Boolean(normalizedProposalId) && (isInspector ? Boolean(currentAccountId) : plannedInspectorOptions.length > 0);

  const renderTaskItem = (task: ResolvedTask, nested = false) => {
    const bulkProgress = task.attributeId ? bulkProgressByAttributeId[task.attributeId] : undefined;
    const savedResponseCount = task.bulkReportEntry && bulkProgress
      ? bulkProgress.savedResponseCount
      : task.savedResponseCount;
    const progress = resolveResponseProgress(savedResponseCount, task.qtyCount);
    const reportFormatAvailability = resolveReportFormatAvailability(
      reportFormatStatusByAttributeId?.get(task.attributeId ?? 0),
    );

    return (
      <div
        key={task.key}
        style={{
          border: nested ? '1px solid #f5f5f5' : '1px solid #f0f0f0',
          borderRadius: 8,
          padding: nested ? '12px 14px' : '14px 16px',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          background: nested ? '#fff' : undefined,
        }}
      >
        <Text strong style={{ fontSize: 15 }}>
          {task.attributeName}
        </Text>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <Text style={{ fontSize: 13 }}>Adet: {progress.text}</Text>
          <Space size={8} wrap>
            {task.statuses.length ? (
              task.statuses.map((status) => (
                <Tag key={status} color='blue' style={{ marginInlineEnd: 0 }}>
                  {resolveStatusLabel(status)}
                </Tag>
              ))
            ) : (
              <Tag style={{ marginInlineEnd: 0 }}>-</Tag>
            )}
            {task.hasExtra ? (
              <Tag color='orange' style={{ marginInlineEnd: 0 }}>
                SAHADA EKLENDİ
              </Tag>
            ) : null}
            {task.correctionCaseTypes.map((caseType) => {
              const tag = resolveCorrectionCaseTypeLabel(caseType);
              return (
                <Tag key={caseType} color={tag.color} style={{ marginInlineEnd: 0 }}>
                  {tag.label}
                </Tag>
              );
            })}
            <Tooltip title={progress.tooltip}>
              <Tag color={progress.color} style={{ marginInlineEnd: 0 }}>
                {progress.label}
              </Tag>
            </Tooltip>
            <Tooltip title={reportFormatAvailability.tooltip}>
              <Tag color={reportFormatAvailability.color} style={{ marginInlineEnd: 0 }}>
                {reportFormatAvailability.label}
              </Tag>
            </Tooltip>
            <Tooltip title={!reportFormatAvailability.canUse ? reportFormatAvailability.tooltip : undefined}>
              <span>
                <Button
                  size='small'
                  onClick={() => openExtraModal('increase', task)}
                  disabled={!canCreateExtra || !reportFormatAvailability.canUse}
                >
                  Ekle
                </Button>
              </span>
            </Tooltip>
            <Tooltip title={!reportFormatAvailability.canUse ? reportFormatAvailability.tooltip : undefined}>
              <span>
                <Button
                  type='primary'
                  size='small'
                  onClick={() => handleOpenTask(task)}
                  disabled={!normalizedProposalId || !task.controlItems.length || !reportFormatAvailability.canUse}
                >
                  Kontrol
                </Button>
              </span>
            </Tooltip>
          </Space>
        </div>
      </div>
    );
  };

  return (
    <>
      <Card
        className={className}
        title={title}
        extra={
          <Space size={8} wrap>
            <Button
              icon={<DownloadOutlined />}
              loading={downloadingAllReports}
              disabled={!canDownloadPlanReports || downloadingAllReports}
              onClick={openArchiveSelectionDrawer}
            >
              Tüm Raporları İndir
            </Button>
            <Button type='primary'
              onClick={() => openExtraModal('add')}
              disabled={!canCreateExtra || !hasAddableReportFormatOption}
            >
              Yeni Parametre Ekle
            </Button>
          </Space>
        }
      >
        {resolvedTasks.length ? (
          <Space direction='vertical' size={12} style={{ width: '100%' }}>
            {taskComponentGroups.groups.map((group) => (
              <div
                key={group.key}
                style={{
                  border: '1px solid #d9e8ff',
                  borderRadius: 8,
                  padding: '14px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                  background: '#f8fbff',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                  }}
                >
                  <Text type='secondary' style={{ fontSize: 12 }}>
                    Bileşen
                  </Text>
                  <Text strong style={{ fontSize: 15 }}>
                    {group.componentName}
                  </Text>
                </div>
                <Space direction='vertical' size={10} style={{ width: '100%' }}>
                  {group.tasks.map((task) => renderTaskItem(task, true))}
                </Space>
              </div>
            ))}
            {taskComponentGroups.ungrouped.map((task) => renderTaskItem(task))}
          </Space>
        ) : (
          <Text>Görev bulunamadı.</Text>
        )}
      </Card>
      <Drawer
        width={500}
        destroyOnClose
        open={archiveSelectionDrawerOpen}
        onClose={() => {
          if (!downloadingAllReports) {
            setArchiveSelectionDrawerOpen(false);
          }
        }}
        title='ZIP Rapor Arşivi'
        extra={
          <Space>
            <Button
              onClick={() => setArchiveSelectionDrawerOpen(false)}
              disabled={downloadingAllReports}
            >
              Vazgeç
            </Button>
            <Button
              type='primary'
              onClick={handleDownloadAllPlanReports}
              loading={downloadingAllReports}
              disabled={!selectedArchiveAccountIds.length}
            >
              İndir
            </Button>
          </Space>
        }
      >
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          <Text>ZIP dosyasına eklenecek P.K.M. uzmanlarını seçiniz.</Text>
          <Select
            mode='multiple'
            aria-label='P.K.M. uzmanları'
            disabled={isInspector}
            showSearch
            placeholder='P.K.M. uzmanı seçiniz'
            value={selectedArchiveAccountIds}
            options={plannedInspectorOptions}
            onChange={(accountIds) => setSelectedArchiveAccountIds(accountIds)}
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
            }
          />
        </Space>
      </Drawer>
      <Drawer
        width={500}
        destroyOnClose
        open={drawerVisible}
        onClose={handleCloseDrawer}
        title={drawerTitle}
        extra={
          <Tooltip title='Tümünü indir'>
            <span>
              <Button
                aria-label='Tümünü İndir'
                icon={<DownloadOutlined />}
                onClick={handleDownloadAllReports}
                disabled={!canDownloadAllReports || downloadingAllReports}
                loading={downloadingAllReports}
              />
            </span>
          </Tooltip>
        }
      >
        {activeTask ? (
          activeTask.bulkReportEntry ? (
            <Space direction='vertical' size={16} style={{ width: '100%' }}>
              <Text strong>Görev Sayısı: {bulkCapacity?.totalQuantity ?? activeTask.qtyCount}</Text>
              <Card size='small' title='Yeni Toplu Giriş'>
                <Space direction='vertical' size={12} style={{ width: '100%' }}>
                  {bulkCapacityLoading ? <Text type='secondary'>Toplu rapor kotası yükleniyor…</Text> : null}
                  {bulkCapacityError ? <Text type='danger'>{bulkCapacityError}</Text> : null}
                  <Form.Item label='Rapor Sayısı' style={{ marginBottom: 0 }}>
                    <InputNumber
                      min={hasBulkCapacity && (bulkReportCountLimit ?? 0) <= 0 ? 0 : 1}
                      max={bulkReportCountLimit}
                      precision={0}
                      value={bulkReportCountDraft}
                      disabled={!hasBulkCapacity || (bulkReportCountLimit ?? 0) <= 0}
                      style={{ width: '100%' }}
                      onChange={(value) => {
                        if (value === null) {
                          setBulkReportCountDraft(null);
                          return;
                        }
                        const nextValue = Math.max(Math.floor(Number(value) || 1), 1);
                        setBulkReportCountDraft(
                          hasBulkCapacity
                            ? Math.min(nextValue, Math.max(bulkReportCountLimit ?? 0, 0))
                            : nextValue,
                        );
                      }}
                    />
                  </Form.Item>
                  <Button
                    type='primary'
                    onClick={handleStartBulkControl}
                    disabled={
                      bulkReportCountDraft === null ||
                      !hasBulkCapacity ||
                      (bulkReportCountLimit ?? 0) <= 0
                    }
                    block
                  >
                    Yeni Toplu Giriş
                  </Button>
                </Space>
              </Card>
              <Card size='small' title='Toplu Girişler'>
                <List
                  loading={bulkGroupsLoading}
                  dataSource={bulkGroups}
                  locale={{ emptyText: 'Kayıtlı toplu giriş bulunamadı.' }}
                  renderItem={(group, index) => {
                    const reportCount = Math.max(
                      Math.floor(Number(group.reportCount ?? group.savedResponseCount) || 0),
                      0,
                    );
                    const savedResponseCount = Math.max(Math.floor(Number(group.savedResponseCount) || 0), 0);
                    const updatedAt = group.updatedAt ? new Date(group.updatedAt) : null;
                    const updatedAtLabel = updatedAt && !Number.isNaN(updatedAt.getTime())
                      ? updatedAt.toLocaleString('tr-TR')
                      : '-';
                    const bulkGroupId = toPositiveNumber(group?.id);
                    const isDownloadingBulkGroup = bulkGroupId !== undefined && downloadingBulkGroupId === bulkGroupId;
                    const isDeletingBulkGroup = bulkGroupId !== undefined && deletingBulkGroupId === bulkGroupId;
                    return (
                      <List.Item
                        key={bulkGroupId ?? index}
                        actions={[
                          ...(group.hasNonconformity ? [renderNonconformityIcon(`nonconformity-${bulkGroupId ?? index}`)] : []),
                          <Button
                            key='edit'
                            type='link'
                            icon={<EditOutlined />}
                            onClick={() => handleEditBulkControl(group)}
                          >
                            Düzenle
                          </Button>,
                          <Button
                            key='download'
                            type='link'
                            icon={<DownloadOutlined />}
                            loading={isDownloadingBulkGroup}
                            disabled={savedResponseCount <= 0 || isDownloadingBulkGroup}
                            onClick={() => handleDownloadBulkControl(group, index)}
                          >
                            İndir
                          </Button>,
                          <Popconfirm
                            key='delete'
                            title='Toplu giriş silinsin mi?'
                            description='Bu toplu girişteki tüm kayıtlar kalıcı olarak silinecek.'
                            okText='Sil'
                            cancelText='Vazgeç'
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDeleteBulkControl(group)}
                          >
                            <Tooltip title='Sil'>
                              <span>
                                <Button
                                  aria-label='Toplu Girişi Sil'
                                  type='link'
                                  danger
                                  icon={<DeleteOutlined />}
                                  loading={isDeletingBulkGroup}
                                  disabled={bulkGroupId === undefined || isDeletingBulkGroup}
                                />
                              </span>
                            </Tooltip>
                          </Popconfirm>,
                        ]}
                      >
                        <List.Item.Meta
                          title={`${index + 1}. Toplu Giriş`}
                          description={
                            <Space direction='vertical' size={2}>
                              <Text type='secondary'>Rapor Sayısı: {reportCount || '-'}</Text>
                              <Text type='secondary'>Kayıt Sayısı: {savedResponseCount}</Text>
                              <Text type='secondary'>Son Güncelleme: {updatedAtLabel}</Text>
                            </Space>
                          }
                        />
                      </List.Item>
                    );
                  }}
                />
              </Card>
            </Space>
          ) : drawerItems.length ? (
            <List
              dataSource={drawerItems}
              renderItem={(item) => {
                const missingControlInfo = normalizedProposalId === undefined ||
                  item.attributeId === undefined ||
                  item.taskId === undefined;
                const reportFormatAvailability = resolveReportFormatAvailability(
                  reportFormatStatusByAttributeId?.get(item.attributeId ?? 0),
                );
                const signedReportActionLabel = item.signedReportUrl ? 'E-imzalı raporu düzenle' : 'E-imzalı rapor yükle';
                const signedReportMenuItems = [
                  {
                    key: 'view',
                    icon: <EyeOutlined />,
                    label: 'Görüntüle',
                    disabled: !item.signedReportUrl,
                  },
                  {
                    key: 'download',
                    icon: <DownloadOutlined />,
                    label: 'İndir',
                    disabled: !item.signedReportUrl,
                  },
                ];
                return (
                <List.Item
                  key={item.key}
                  actions={[
                    ...(item.hasNonconformity ? [renderNonconformityIcon(`nonconformity-${item.key}`)] : []),
                    <Space key='actions' size={12}>
                      {renderIconButton(
                        reportFormatAvailability.canUse
                          ? (item.hasSavedResponse ? 'Kontrolü düzenle' : 'Kontrol başlat')
                          : reportFormatAvailability.tooltip,
                        item.hasSavedResponse ? 'Düzenle' : 'Kontrol Başlat',
                        item.hasSavedResponse ? <EditOutlined /> : <PlayCircleOutlined />,
                        () => handleStartControl(item),
                        missingControlInfo || !reportFormatAvailability.canUse,
                        'primary',
                      )}
                      {item.hasSavedResponse ? renderIconButton(
                        item.reportUrl ? 'Rapor PDF dosyasını aç' : 'Rapor PDF dosyası henüz oluşturulmamış',
                        'Raporu Aç',
                        <FilePdfOutlined />,
                        () => handleDownloadReport(item),
                        !item.reportUrl,
                      ) : null}
                      {item.hasSavedResponse ? (
                        <Space.Compact>
                          {renderIconButton(
                            signedReportActionLabel,
                            item.signedReportUrl ? 'E-imzalı Raporu Düzenle' : 'E-imzalı Rapor Yükle',
                            item.signedReportUrl ? <EditOutlined /> : <UploadOutlined />,
                            () => handleOpenUploadDrawer(item),
                            missingControlInfo,
                          )}
                          <Tooltip title={item.signedReportUrl ? 'E-imzalı rapor seçenekleri' : 'E-imzalı rapor yüklenmemiş'}>
                            <span>
                              <Dropdown
                                menu={{
                                  items: signedReportMenuItems,
                                  onClick: ({ key }) => {
                                    if (key === 'view') {
                                      openReportUrl(item.signedReportUrl);
                                    }
                                    if (key === 'download') {
                                      downloadReportUrl(item.signedReportUrl);
                                    }
                                  },
                                }}
                                trigger={['click']}
                                disabled={!item.signedReportUrl}
                              >
                                <Button
                                  aria-label='E-imzalı rapor seçenekleri'
                                  icon={<MoreOutlined />}
                                  disabled={!item.signedReportUrl}
                                />
                              </Dropdown>
                            </span>
                          </Tooltip>
                        </Space.Compact>
                      ) : null}
                    </Space>,
                  ]}
                >
                  <List.Item.Meta
                    title={`${item.displayOrder}. ${activeTask.attributeName}`}
                    description={
                      <Space direction='vertical' size={4}>
                        <Text style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                          Kontrol sırası #{item.displayOrder}
                        </Text>
                        {item.isExtra ? (
                          <Tag color='orange' style={{ width: 'fit-content' }}>
                            SAHADA EKLENDİ
                          </Tag>
                        ) : null}
                        {item.isCorrection ? (
                          <Space direction='vertical' size={4}>
                            <Tag color={resolveCorrectionCaseTypeLabel(item.correctionCaseType).color} style={{ width: 'fit-content' }}>
                              {resolveCorrectionCaseTypeLabel(item.correctionCaseType).label}
                            </Tag>
                            <Text type='secondary' style={{ fontSize: 12 }}>
                              Önceki Rapor: {item.sourceReportNumber || '-'}
                            </Text>
                          </Space>
                        ) : null}
                        {item.hasSavedResponse ? (
                          <Tag color='green' style={{ width: 'fit-content' }}>
                            Daha önce kaydedildi
                          </Tag>
                        ) : null}
                      </Space>
                    }
                  />
                </List.Item>
                );
              }}
            />
          ) : (
            <Text>Bu görev için belirlenmiş kontrol adedi bulunmuyor.</Text>
          )
        ) : null}
      </Drawer>
      <Drawer
        width={520}
        destroyOnClose
        open={Boolean(uploadItem)}
        onClose={handleCloseUploadDrawer}
        title='Rapor Yükle'
        extra={
          <Space>
            <Button onClick={handleCloseUploadDrawer}>İptal</Button>
            <Button
              type='primary'
              onClick={handleSaveSignedReport}
              loading={uploadingSignedReport}
              disabled={!signedReportFileList.length}
            >
              Kaydet
            </Button>
          </Space>
        }
      >
        <Space direction='vertical' size={16} style={{ width: '100%' }}>
          {uploadItem?.signedReportUrl ? (
            <Button onClick={() => openReportUrl(uploadItem.signedReportUrl)}>
              Yüklü Raporu Görüntüle
            </Button>
          ) : (
            <Text type='secondary'>Henüz e-imzalı rapor yüklenmemiş.</Text>
          )}
          <div onPaste={handleSignedReportPaste}>
            <Upload.Dragger
              accept='.pdf,application/pdf'
              maxCount={1}
              multiple={false}
              fileList={signedReportFileList}
              beforeUpload={(file) => {
                if (!validateSignedReportFile(file)) {
                  return Upload.LIST_IGNORE;
                }
                return false;
              }}
              onChange={({ fileList }) => setSignedReportFileList(fileList.slice(-1))}
              disabled={uploadingSignedReport}
            >
              <Space direction='vertical' size={6} style={{ width: '100%' }}>
                <Text>PDF dosyasını sürükleyip bırakın, seçin veya Ctrl+V ile yapıştırın.</Text>
                <Text type='secondary'>Yalnızca PDF dosyası yüklenebilir.</Text>
              </Space>
            </Upload.Dragger>
          </div>
        </Space>
      </Drawer>
      <Modal
        open={extraModalOpen}
        title={extraMode === 'increase' ? 'Ekle' : 'Yeni Parametre Ekle'}
        okText='Kaydet'
        cancelText='İptal'
        confirmLoading={savingExtra}
        onOk={handleSaveExtraTask}
        onCancel={closeExtraModal}
        destroyOnClose
      >
        <Form form={form} layout='vertical'>
          {!isInspector ? (
            <Form.Item name='accountId' label='Uzman' rules={[{ required: true, message: 'Uzman seçiniz.' }]}>
              <Select
                showSearch
                placeholder='Uzman seçiniz'
                options={plannedInspectorOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
                }
              />
            </Form.Item>
          ) : null}
          {extraMode === 'add' ? (
            <Form.Item name='attributeId' label='Parametre' rules={[{ required: true, message: 'Parametre seçiniz.' }]}>
              <Select
                showSearch
                placeholder='Parametre seçiniz'
                options={addableAttributeOptions}
                filterOption={(input, option) =>
                  (option?.label ?? '').toString().toLocaleLowerCase('tr').includes(input.toLocaleLowerCase('tr'))
                }
              />
            </Form.Item>
          ) : (
            <Form.Item label='Parametre'>
              <Text>{extraTarget?.attributeName ?? '-'}</Text>
            </Form.Item>
          )}
          <Form.Item name='qty' label='Artış Adedi' rules={[{ required: true, message: 'Adet giriniz.' }]}>
            <InputNumber min={1} precision={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default InspectorPlanTasksCard;
